import axiosInstance from './AxiosInstance';

const LoansService = {
    /**
     * Get active standalone ledger entries (credits) for a loan
     * @param {number} loanId - The loan ID
     * @param {Object} params - Query parameters (startDate, endDate, type, page, limit, sort)
     * @returns {Promise} Response with active standalone ledger entries
     */
    getActiveStandaloneLedger: async (loanId, params = {}) => {
        try {
            const response = await axiosInstance.get(`/loan-ledger/${loanId}/active-standalone`, { params });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch active standalone ledger entries";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },
    // ============================================
    // LOAN APPLICATION CRUD OPERATIONS
    // ============================================

    /**
     * Create a new loan application (Step 0)
     * @returns {Promise} Response with loan application data
     */
    createLoanApplication: async () => {
        try {
            const response = await axiosInstance.post('/loans', {});
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to create loan application";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Create a new loan application with customer data (Step 0-1)
     * @param {Object} customerData - Customer information for loan creation
     * @param {string} customerData.name - Customer name (2-200 chars)
     * @param {string} customerData.gender - "Male", "Female", or "Other"
     * @param {string} customerData.aadharNumber - 12 digit Aadhar number
     * @param {string} customerData.fatherName - Father's name (optional)
     * @param {string} customerData.motherName - Mother's name (optional)
     * @param {string} customerData.wifeName - Wife's name (optional)
     * @param {string} customerData.homeTown - Home town (optional)
     * @param {Array} customerData.addresses - Array of addresses (min 1)
     * @param {Array} customerData.mobileNumbers - Array of mobile numbers (min 1)
     * @returns {Promise} Response with loan application data including customer
     */
    createLoanWithCustomer: async (customerData) => {
        try {
            const response = await axiosInstance.post('/loans', customerData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to create loan with customer";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Get loan application by loan number
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with full loan application details
     */
    getLoanByNumber: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(`/loans/${loanApplicationNumber}`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch loan";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Create a loan note
     * @param {Object} noteData - Note payload
     * @param {string} noteData.loanApplicationNumber - Loan application number
     * @param {string} noteData.noteDate - Note date in YYYY-MM-DD format
     * @param {string} noteData.description - Note description
     * @returns {Promise} Response from note creation endpoint
     */
    createLoanNote: async (noteData) => {
        try {
            const response = await axiosInstance.post('/notes', noteData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to create loan note";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get loan notes by application number
     * @param {Object} params
     * @param {string} params.loanApplicationNumber - Loan application number
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.limit=20] - Page size
     * @returns {Promise} Response with notes and pagination
     */
    getLoanNotes: async ({ loanApplicationNumber, page = 1, limit = 20 }) => {
        try {
            const response = await axiosInstance.get('/notes', {
                params: {
                    loanApplicationNumber,
                    page,
                    limit,
                },
            });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch loan notes";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Update an existing loan note
     * @param {number|string} noteId - Note ID
     * @param {Object} noteData - Updated note fields
     * @param {string} noteData.noteDate - Note date in YYYY-MM-DD format
     * @param {string} noteData.description - Note description
     * @returns {Promise} Response from note update endpoint
     */
    updateLoanNote: async (noteId, noteData) => {
        try {
            const response = await axiosInstance.put(`/notes/${noteId}`, noteData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to update loan note";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Update loan application number for an existing loan
     * @param {number|string} loanId - Loan ID
     * @param {Object} data - Payload containing the new loan application number
     * @param {string} data.loanApplicationNumber - New loan application number
     * @returns {Promise} Response from update endpoint
     */
    updateLoanApplicationNumber: async (loanId, data) => {
        try {
            const response = await axiosInstance.put(`/loans/${loanId}/application-number`, data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to update loan application number";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Search loan applications by loan number, mobile number, or vehicle registration
     * @param {string} searchTerm - Loan number OR mobile number OR vehicle registration
     * @returns {Promise} Response with array of matching loans { status, message, count, data: [...] }
     */
    searchLoan: async (searchTerm) => {
        try {
            const response = await axiosInstance.get(`/loans/search/${encodeURIComponent(searchTerm)}`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to search loan";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get loan application summary (lightweight)
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with loan summary
     */
    getLoanSummary: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(`/loans/${loanApplicationNumber}/summary`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch loan summary";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get neighbor approved loans (previous and next) scoped by company/branch from auth context
     * @param {string} loanApplicationNumber
     * @returns {Promise} Response with { previous, next }
     */
    getNeighborLoans: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(`/loans/${loanApplicationNumber}/neighbor`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch neighbor loans";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get all loan applications with filters and pagination
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.limit - Items per page (default: 10, max: 100)
     * @param {string} params.status - Filter by status
     * @param {number} params.customerId - Filter by customer ID
     * @param {string} params.loanApplicationNumber - Partial match search
     * @returns {Promise} Response with paginated loans list
     */
    getAllLoans: async (params = {}) => {
        try {
            console.log('paramsparams',params)
            const queryParams = new URLSearchParams();
            
            // Add pagination parameters
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            
            // Add filter parameters
            if (params.status) queryParams.append('status', params.status);
            if (params.search) queryParams.append('search', params.search);
            if (params.customerId) queryParams.append('customerId', params.customerId);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            if (params.companyId) queryParams.append('companyId', params.companyId);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.customerName) queryParams.append('customerName', params.customerName);
            if (params.customerMobile) queryParams.append('customerMobile', params.customerMobile);
            if (params.fromDate) queryParams.append('fromDate', params.fromDate);
            if (params.toDate) queryParams.append('toDate', params.toDate);
            if (params.vehicleNumber) queryParams.append('vehicleNumber', params.vehicleNumber);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            
            
            const queryString = queryParams.toString();
            const url = queryString ? `/loans?${queryString}` : '/loans';
            
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch loans";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Download EMI details as XLSX (returns blob)
     * @param {Object} params - Query params { startDate, endDate }
     * @returns {Promise} Axios response with blob data
     */
    downloadEmiDetailsXlsx: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            const queryString = queryParams.toString();
            const url = queryString ? `/receipts/details/download/xlsx?${queryString}` : '/receipts/details/download/xlsx';
            // request as blob so client can save the XLSX
            const response = await axiosInstance.get(url, { responseType: 'blob', headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } });
            return response;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to download EMI XLSX";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Download loans list as XLSX (returns blob)
     * @param {Object} params - Query params { status, customerId, loanApplicationNumber, branchId, fromDate, toDate, companyId }
     * @returns {Promise} Axios response with blob data
     */
    downloadLoans: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.status) queryParams.append('status', params.status);
            if (params.customerId) queryParams.append('customerId', params.customerId);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.companyId) queryParams.append('companyId', params.companyId);
            if (params.fromDate) queryParams.append('fromDate', params.fromDate);
            if (params.toDate) queryParams.append('toDate', params.toDate);

            const queryString = queryParams.toString();
            const url = queryString ? `/loans/download/xlsx?${queryString}` : '/loans/download/xlsx';

            const response = await axiosInstance.get(url, { responseType: 'blob', headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' } });
            return response;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to download loans XLSX";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Copy loan application data from source to target
     * @param {string} sourceLoanNumber
     * @param {string} targetLoanNumber
     * @returns {Promise} Response with copy result
     */
    copyLoan: async (sourceLoanNumber, targetLoanNumber) => {
        try {
            const response = await axiosInstance.post('/loans/copy', {
                sourceLoanNumber,
                targetLoanNumber,
            });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to copy loan";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Cancel loan application
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with cancellation confirmation
     */
    cancelLoan: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.delete(`/loans/${loanApplicationNumber}`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to cancel loan";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // ============================================
    // LOAN APPLICATION STEPS - GET APIs
    // ============================================

    /**
     * Get customer data for a loan application (Step 1 - GET)
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with customer data or empty state
     */
    getCustomerStep: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(`/loans/${loanApplicationNumber}/step/customer`);
            console.log('getCustomerStep', response.data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch customer step";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get guarantor data for a loan application (Step 3 - GET)
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with guarantor data or empty state
     */
    getGuarantorStep: async (loanApplicationNumber) => {
        try {
            // New API: GET /loans/:loanApplicationNumber/guarantors
            const response = await axiosInstance.get(`/loans/${loanApplicationNumber}/guarantors`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch guarantor step";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get vehicle data for a loan application (Step 4 - GET)
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with vehicle data or empty state
     */
    getVehicleStep: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(`/loans/${loanApplicationNumber}/step/vehicle`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch vehicle step";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get loan details for a loan application (Step 5 - GET)
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with loan details or empty state
     */
    getLoanDetailsStep: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(`/loans/${loanApplicationNumber}/step/details`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch loan details step";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // ============================================
    // LOAN APPLICATION STEPS - POST/PUT APIs
    // ============================================

    /**
     * Add customer to loan application (Step 1)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} customerData - Customer information
     * @param {string} customerData.name - Customer name (2-200 chars)
     * @param {string} customerData.gender - "Male", "Female", or "Other"
     * @param {string} customerData.fatherName - Father's name (optional)
     * @param {string} customerData.motherName - Mother's name (optional)
     * @param {string} customerData.wifeName - Wife's name (optional)
     * @param {string} customerData.aadharNumber - 12 digit Aadhar number
     * @param {string} customerData.homeTown - Home town (optional)
     * @param {Array} customerData.addresses - Array of addresses (min 1)
     * @param {Array} customerData.mobileNumbers - Array of mobile numbers (min 1)
     * @returns {Promise} Response with updated loan data
     */
    addCustomer: async (loanApplicationNumber, customerData) => {
        try {
            const response = await axiosInstance.put(`/loans/${loanApplicationNumber}/customer`, customerData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to add customer";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Add KYC to loan application (Step 2)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} kycData - KYC information
     * @param {string} kycData.aadharCardNo - 12 digit Aadhar number
     * @param {string} kycData.aadharCard - Aadhar card URL (optional)
     * @param {string} kycData.panCard - PAN card number (optional)
     * @param {string} kycData.photo - Photo URL (optional)
     * @param {string} kycData.lightBillPhoto - Light bill photo URL (optional)
     * @param {string} kycData.rationCard - Ration card number (optional)
     * @returns {Promise} Response with updated loan data
     */
    addKyc: async (loanApplicationNumber, kycData) => {
        try {
            const response = await axiosInstance.put(`/loans/${loanApplicationNumber}/kyc`, kycData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to add KYC";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Add guarantor to loan application (Step 3)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} guarantorData - Guarantor information (same structure as customer + relationship)
     * @param {string} guarantorData.relationship - Relationship to customer (2-100 chars)
     * @returns {Promise} Response with updated loan data
     */
    addGuarantor: async (loanApplicationNumber, guarantorData) => {
        try {
            // New API expects an array of guarantors at PUT /loans/:loanApplicationNumber/guarantors
            const payload = Array.isArray(guarantorData) ? guarantorData : [guarantorData];
            const response = await axiosInstance.put(`/loans/${loanApplicationNumber}/guarantors`, payload);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to add guarantor";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Add vehicle to loan application (Step 4)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} vehicleData - Vehicle information
     * @param {string} vehicleData.vehicleName - Vehicle name (2-200 chars)
     * @param {string} vehicleData.vehicleType - "Two Wheeler", "Four Wheeler", "Commercial Vehicle", or "Other"
     * @param {string} vehicleData.registrationNumber - Registration number (5-20 chars)
     * @param {string} vehicleData.chassisNo - Chassis number (5-50 chars)
     * @param {string} vehicleData.engineNo - Engine number (5-50 chars)
     * @param {string} vehicleData.company - Vehicle company (optional)
     * @param {string} vehicleData.model - Vehicle model (optional)
     * @returns {Promise} Response with updated loan data
     */
    addVehicle: async (loanApplicationNumber, vehicleData) => {
        try {
            const response = await axiosInstance.put(`/loans/${loanApplicationNumber}/vehicle`, vehicleData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to add vehicle";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Add loan details (Step 5)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} loanDetails - Loan details information
     * @param {number} loanDetails.loanAmount - Loan amount (positive, max 2 decimals)
     * @param {string} loanDetails.interestType - "Flat", "Reducing", or "Simple"
     * @param {number} loanDetails.interestRate - Interest rate (0-100, max 2 decimals)
     * @param {number} loanDetails.emiMonths - EMI months (1-360)
     * @param {number} loanDetails.emiAmount - EMI amount (positive, max 2 decimals)
     * @param {number} loanDetails.processingFees - Processing fees (optional, >= 0)
     * @returns {Promise} Response with updated loan data
     */
    addLoanDetails: async (loanApplicationNumber, loanDetails) => {
        try {
            const response = await axiosInstance.put(`/loans/${loanApplicationNumber}/details`, loanDetails);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to add loan details";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    // ============================================
    // LOAN APPROVAL/REJECTION
    // ============================================

    /**
     * Approve loan application (Step 6a)
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with approval confirmation
     */
    approveLoan: async (loanApplicationNumber, data = {}) => {
        try {
            const response = await axiosInstance.post(`/loans/${loanApplicationNumber}/approve`, data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to approve loan";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Reject loan application (Step 6b)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {string} reason - Rejection reason (10-1000 chars)
     * @returns {Promise} Response with rejection confirmation
     */
    rejectLoan: async (loanApplicationNumber, reason) => {
        try {
            const response = await axiosInstance.post(`/loans/${loanApplicationNumber}/reject`, { reason });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to reject loan";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Modify loan application (change EMI months, loan amount, or interest rate)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} modificationData - Modification data
     * @param {number} modificationData.newLoanAmount - Optional: New total loan amount
     * @param {number} modificationData.newEmiMonths - Optional: New total EMI months (1-360)
     * @param {number} modificationData.newInterestRate - Optional: New interest rate
     * @param {string} modificationData.reason - Required: Reason for modification (10-500 chars)
     * @returns {Promise} Response with modification details
     */
    modifyLoan: async (loanApplicationNumber, modificationData) => {
        try {
            const response = await axiosInstance.post(`/loans/${loanApplicationNumber}/modify`, modificationData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to modify loan";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    /**
     * Approve loan application with optional rounding (Step 6a - Enhanced)
     * When applyRounding is true, all EMI principal and interest amounts are rounded to nearest 10
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} data - Approval data
     * @param {string} data.approvedDate - Optional: Approval date (YYYY-MM-DD or ISO format, default: current date)
     * @param {boolean} data.applyRounding - Optional: Whether to round principal/interest to nearest 10 (default: false)
     * @returns {Promise} Response with approval confirmation and rounding details
     */
    approveLoanWithRounding: async (loanApplicationNumber, data = {}) => {
        try {
            const response = await axiosInstance.post(`/loans/${loanApplicationNumber}/approve-with-rounding`, data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to approve loan with rounding";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Modify approved loan with optional rounding
     * When applyRounding is true, only the NEW unpaid EMIs are rounded to nearest 10
     * Paid EMIs are not modified. Difference is tracked in differenceAmount column.
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} modificationData - Modification data
     * @param {number} modificationData.newLoanAmount - Optional: New total loan amount
     * @param {number} modificationData.newEmiMonths - Optional: New total EMI months (1-360)
     * @param {number} modificationData.newInterestRate - Optional: New interest rate
     * @param {string} modificationData.reason - Required: Reason for modification (min 10 chars)
     * @param {boolean} modificationData.applyRounding - Optional: Whether to round new EMI principal/interest (default: false)
     * @returns {Promise} Response with modification details and rounding information
     */
    modifyLoanWithRounding: async (loanApplicationNumber, modificationData) => {
        try {
            const response = await axiosInstance.post(`/loans/${loanApplicationNumber}/modify-with-rounding`, modificationData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to modify loan with rounding";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    // ============================================
    // STATISTICS & REPORTS
    // ============================================

    /**
     * Get company loan statistics
     * @param {Object} params - Query parameters for filtering statistics
     * @returns {Promise} Response with loan statistics
     */
    getCompanyStatistics: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filter parameters if provided
            if (params.companyId) queryParams.append('companyId', params.companyId);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            
            const queryString = queryParams.toString();
            const url = queryString ? `/loans/statistics/company?${queryString}` : '/loans/statistics/company';
            const response = await axiosInstance.get(url);
            
            // Return response data as-is
            return response.data || {
                status: 'success',
                data: {
                    totalLoans: 0,
                    pendingApproval: 0,
                    approvedLoans: 0,
                    rejectedLoans: 0,
                    cancelledLoans: 0,
                    draftLoans: 0,
                    totalLoanAmount: 0,
                    totalApprovedAmount: 0
                }
            };
        } catch (error) {
            console.error('Error in getCompanyStatistics:', error);
            return {
                status: 'error',
                message: error.response?.data?.message || error.message || "Failed to fetch loan statistics",
                data: {
                    totalLoans: 0,
                    pendingApproval: 0,
                    approvedLoans: 0,
                    rejectedLoans: 0,
                    cancelledLoans: 0,
                    draftLoans: 0,
                    totalLoanAmount: 0,
                    totalApprovedAmount: 0
                }
            };
        }
    },

    /**
     * Get pending loans by age
     * @returns {Promise} Response with pending loans breakdown
     */
    getPendingByAge: async () => {
        try {
            const response = await axiosInstance.get('/loans/statistics/pending-by-age');
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch pending loans by age";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // ============================================
    // EMI DETAILS OPERATIONS
    // ============================================

    /**
     * Get EMI details from multiple loans with filtering
     * @param {Object} params - Filter parameters
     * @param {number} params.page - Page number for pagination (default: 1)
     * @param {number} params.limit - Records per page (default: 10, max: 100)
     * @param {number} params.branchId - Filter by branch ID
     * @param {number} params.emiNumber - Filter by specific EMI number
     * @param {string} params.receiptNo - Partial match on receipt number
     * @param {string} params.loanApplicationNumber - Partial match on loan application number
     * @param {string} params.emiStatus - Filter by EMI status (Unpaid, Paid, Overdue, PartiallyPaid)
     * @param {string} params.loanStatus - Filter by loan status (Draft, Pending, Approved, Rejected, Disbursed, Closed, Written Off)
     * @param {string} params.startDate - Start date for EMI due date range (YYYY-MM-DD format)
     * @param {string} params.endDate - End date for EMI due date range (YYYY-MM-DD format)
     * @returns {Promise} Response with EMI details list and pagination
     */
    getEmiDetails: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add all filter parameters if they exist
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.emiNumber) queryParams.append('emiNumber', params.emiNumber);
            if (params.receiptNo) queryParams.append('receiptNo', params.receiptNo);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            if (params.emiStatus) queryParams.append('emiStatus', params.emiStatus);
            if (params.loanStatus) queryParams.append('loanStatus', params.loanStatus);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            if (params.noPagination) queryParams.append('noPagination', 'true');
            console.log('paramsparams', params)
            const queryString = queryParams.toString();
            const url = queryString ? `/receipts/details?${queryString}` : '/receipts/details';
            
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch EMI details";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    getListEmiDetailsWithoutPagination: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add all filter parameters if they exist
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.emiNumber) queryParams.append('emiNumber', params.emiNumber);
            if (params.receiptNo) queryParams.append('receiptNo', params.receiptNo);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            if (params.emiStatus) queryParams.append('emiStatus', params.emiStatus);
            if (params.loanStatus) queryParams.append('loanStatus', params.loanStatus);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            queryParams.append('noPagination', "true");
            const queryString = queryParams.toString();
            const url = queryString ? `/receipts/emi-details?${queryString}` : '/receipts/emi-details';
            
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch EMI details";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    getPaidEmiDetails: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.receiptNo) queryParams.append('receiptNo', params.receiptNo);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            if (params.loanStatus) queryParams.append('loanStatus', params.loanStatus);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);

            // Default to no pagination unless explicitly disabled
            if (params.noPagination === false || params.noPagination === 'false') {
                // if caller explicitly disables it, do nothing here
            } else {
                queryParams.append('noPagination', 'true');
            }
            if (params.paginate === false || params.paginate === 'false') {
                queryParams.append('paginate', 'false');
            }
            const queryString = queryParams.toString();
            const url = queryString ? `/receipts/emi-details/paid?${queryString}` : '/receipts/emi-details/paid';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch paid EMI details";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    getUnpaidEmiDetails: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.receiptNo) queryParams.append('receiptNo', params.receiptNo);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            if (params.loanStatus) queryParams.append('loanStatus', params.loanStatus);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);

            // Default to no pagination unless explicitly disabled
            if (params.noPagination === false || params.noPagination === 'false') {
                // if caller explicitly disables it, do nothing here
            } else {
                queryParams.append('noPagination', 'true');
            }
            if (params.paginate === false || params.paginate === 'false') {
                queryParams.append('paginate', 'false');
            }
            const queryString = queryParams.toString();
            const url = queryString ? `/receipts/emi-details/unpaid?${queryString}` : '/receipts/emi-details/unpaid';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch unpaid EMI details";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    getListEmiDetails: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add all filter parameters if they exist
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.emiNumber) queryParams.append('emiNumber', params.emiNumber);
            if (params.receiptNo) queryParams.append('receiptNo', params.receiptNo);
            if (params.loanApplicationNumber) queryParams.append('loanApplicationNumber', params.loanApplicationNumber);
            if (params.emiStatus) queryParams.append('emiStatus', params.emiStatus);
            if (params.loanStatus) queryParams.append('loanStatus', params.loanStatus);
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            const queryString = queryParams.toString();
            const url = queryString ? `/receipts/emi-details?${queryString}` : '/receipts/emi-details';
            
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch EMI details";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },


    

    /**
     * Close a loan as loss
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with closure details
     */
    closeLoanAsLoss: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.post(`/emi-closure/${loanApplicationNumber}/close-as-loss`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to close loan as loss";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Revoke a loan loss closure and restore the loan to active status
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with revoke details
     */
    revokeLoanLossClosure: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.post(`/emi-closure/${loanApplicationNumber}/revoke-loss`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to revoke loan loss closure";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Simulate pre-closure calculation with new interest rate
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} data - Simulation data
     * @param {number} data.newInterestRate - New interest rate percentage (0-100)
     * @returns {Promise} Response with pre-closure calculation
     */
    preCloseSimulation: async (loanApplicationNumber, data) => {
        try {
            const response = await axiosInstance.post(`/emi-closure/${loanApplicationNumber}/pre-close-simulation`, data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to simulate pre-closure";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Execute pre-closure with payment
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} data - Pre-closure execution data
     * @param {number} data.newInterestRate - New interest rate percentage (0-100)
     * @param {number} data.paymentAmount - Total payment amount (must match simulation)
     * @param {string} data.paymentMethod - Payment method (Cash, UPI, Card, Cheque, BankTransfer)
     * @param {string} data.paymentDate - Payment date (YYYY-MM-DD or ISO format)
     * @param {string} [data.notes] - Additional notes (optional, max 500 chars)
     * @returns {Promise} Response with pre-closure execution details
     */
    preCloseLoan: async (loanApplicationNumber, data) => {
        try {
            const response = await axiosInstance.post(`/emi-closure/${loanApplicationNumber}/pre-close`, data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to execute pre-closure";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get penalty information for a loan
     * Calculates penalty and loan information based on overdue EMIs
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with penalty and EMI details
     */
    getPenaltyInfo: async (loanId, paymentDate) => {
        try {
            const config = {};
            if (paymentDate) {
                config.params = { paymentDate };
            }
            // original penalty-info endpoint
            const response = await axiosInstance.get(
                `/loans/${loanId}/penalty-info`,
                config,
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch penalty information";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },
    
    /**
     * Get penalty information for a loan based on a selected payment date
     * This uses the newer `/penalty-with-date-info` endpoint which returns the
     * same shape of data as `penalty-info`, but is intended for situations
     * where the user explicitly changes the date via a picker.
     * @param {string} loanApplicationNumber - Loan application number
     * @param {string} paymentDate - Payment date string (YYYY-MM-DD)
     * @returns {Promise} Response with penalty and EMI details
     */
    getPenaltyWithDateInfo: async (loanApplicationNumber, paymentDate, emiNumber) => {
        try {
            const config = {};
            if (paymentDate || emiNumber) {
                config.params = {};
                if (paymentDate) config.params.paymentDate = paymentDate;
                if (emiNumber !== undefined && emiNumber !== null) config.params.emiNumber = emiNumber;
            }
            const response = await axiosInstance.get(
                `/loans/${loanApplicationNumber}/penalty-with-date-info`,
                config,
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch penalty information with date";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get aggregated EMI summary statistics
     * Includes Total Amount, Total Principal, Total Interest, and Total Penalty Paid
     * @param {Object} params - Query parameters
     * @param {string} params.startDate - Filter by start date (YYYY-MM-DD) - optional
     * @param {string} params.endDate - Filter by end date (YYYY-MM-DD) - optional
     * @param {string} params.loanApplicationNumber - Filter by loan application number (partial match) - optional
     * @param {string} params.emiStatus - Filter by EMI status: Paid, Unpaid, Overdue, PartiallyPaid - optional
     * @param {string} params.loanStatus - Filter by loan status - optional
     * @returns {Promise} Response with EMI summary statistics
     */
    getEmiSummary: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/loans/emi-details/summary', { params });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch EMI summary";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    }
,

    /**
     * Get EMI details aggregate (grouped by day/week/month)
     * Calls GET /loans/emi-details/aggregate with params: view, fromDate, toDate, companyId, branchId
     * @param {Object} params
     * @param {string} params.view - day|week|month
     * @param {string} params.fromDate - YYYY-MM-DD
     * @param {string} params.toDate - YYYY-MM-DD
     * @param {number} params.companyId
     * @param {number} params.branchId
     */
    getEmiAggregate: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.view) queryParams.append('view', params.view);
            if (params.fromDate) queryParams.append('fromDate', params.fromDate);
            if (params.toDate) queryParams.append('toDate', params.toDate);
            if (params.companyId) queryParams.append('companyId', params.companyId);
            if (params.branchId) queryParams.append('branchId', params.branchId);

            const qs = queryParams.toString();
            const url = qs ? `/loans/emi-details/aggregate?${qs}` : '/loans/emi-details/aggregate';
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch EMI aggregate";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    }

    ,
    /**
     * Get receipts grouped by month: receiptCount, paidAmount, penaltyAmount,
     * pre-formatted periodLabel ("October-2026") and a totals row — all
     * computed server-side (Receipt Monthly Summary page).
     * Calls GET /receipts/monthly-summary, params: startDate, endDate, companyId, branchId
     * @param {Object} params
     * @param {string} params.startDate - YYYY-MM-DD
     * @param {string} params.endDate - YYYY-MM-DD
     * @param {number} params.companyId
     * @param {number} params.branchId
     */
    getReceiptMonthlySummary: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            if (params.companyId) queryParams.append('companyId', params.companyId);
            if (params.branchId) queryParams.append('branchId', params.branchId);

            const qs = queryParams.toString();
            const url = qs ? `/receipts/monthly-summary?${qs}` : '/receipts/monthly-summary';
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch receipt monthly summary";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    }

    ,
    /**
     * Make EMI payment with credits
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} paymentData - Payment data
     * @param {number} paymentData.paymentAmount - Cash payment amount
     * @param {string} paymentData.paymentDate - Payment date (YYYY-MM-DD)
     * @param {string} paymentData.paymentMethod - Payment method (Cash, UPI, Card, Cheque, BankTransfer)
     * @param {Array<number>} paymentData.selectedCreditIds - Array of credit entry IDs
     * @param {string} [paymentData.notes] - Optional payment notes
     * @returns {Promise} Response with payment confirmation and receipt details
     */
    makeEmiPaymentWithCredits: async (loanApplicationNumber, paymentData) => {
        try {
            const response = await axiosInstance.post(
                `/receipt-payment/${loanApplicationNumber}/emi-payment-with-credits`,
                paymentData
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to process EMI payment with credits";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    }
    ,
    /**
     * Get loans aggregate (grouped by day/week/month)
     * Calls GET /loans/aggregate with params: view, fromDate, toDate, status, companyId, branchId
     * @param {Object} params
     */
    getAggregate: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            if (params.view) queryParams.append('view', params.view);
            if (params.fromDate) queryParams.append('fromDate', params.fromDate);
            if (params.toDate) queryParams.append('toDate', params.toDate);
            if (params.status) queryParams.append('status', params.status);
            if (params.companyId) queryParams.append('companyId', params.companyId);
            if (params.branchId) queryParams.append('branchId', params.branchId);

            const qs = queryParams.toString();
            const url = qs ? `/loans/aggregate?${qs}` : '/loans/aggregate';
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch loans aggregate";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Revert an EMI payment that was made with credits
     * @param {number} emiHistoryId - ID of the EMI history entry to revert
     * @returns {Promise} Response from server
     */
    revertEmiPayment: async (emiHistoryId) => {
        try {
            if (!emiHistoryId || Number.isNaN(Number(emiHistoryId)) || Number(emiHistoryId) <= 0) {
                throw { message: "Invalid emiHistoryId" };
            }
            const response = await axiosInstance.post(
                "/receipt-payment/revert-emi-payment",
                { emiHistoryId: Number(emiHistoryId) }
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to revert EMI payment";
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },

    /**
     * Revert only penalty charges for a single paid EMI
     * @param {number} emiHistoryId - ID of the EMI history entry whose penalty should be reverted
     * @returns {Promise} Response from server
     */
    revertPenalty: async (emiHistoryId) => {
        try {
            if (!emiHistoryId || Number.isNaN(Number(emiHistoryId)) || Number(emiHistoryId) <= 0) {
                throw { message: "Invalid emiHistoryId" };
            }
            const response = await axiosInstance.post(
                "/receipt-payment/revert-penalty",
                { emiHistoryId: Number(emiHistoryId) }
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to revert EMI penalty";
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },

    /**
     * @param {string} receiptNumber - Receipt number (e.g., REC-ABC-MB-0123)
     * @returns {Promise} Response with detailed receipt information
     */
    getReceiptForPrint: async (receiptNumber) => {
        try {
            const response = await axiosInstance.get(`/receipts/print/${receiptNumber}`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch receipt for printing";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    /**
     * Get closure summary for a loan
     * @param {string} loanApplicationNumber - Loan application number
     * @param {number} newInterestRate - New interest rate (optional) for simulation
     * @param {string} closureDate - Closure date (optional, YYYY-MM-DD) to recompute penalty as of that date
     * @returns {Promise} Response with closure summary including pending EMIs, penalties, and final amount
     */
    getClosureSummary: async (loanApplicationNumber, newInterestRate = null, closureDate = null) => {
        try {
            const params = {};
            if (newInterestRate !== null && newInterestRate !== undefined) {
                params.newInterestRate = newInterestRate;
            }
            if (closureDate) {
                params.closureDate = closureDate;
            }
            const response = await axiosInstance.get(`/emi-closure/${loanApplicationNumber}/closure-summary`, { params });
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch closure summary";
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },

    /**
     * Close loan by paying all pending EMIs and penalties
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} payload - Closure payment details
     * @param {number} payload.pendingLoanCollection - Total pending collection
     * @param {number} payload.pendingEMIAmount - Total pending EMI amount
     * @param {number} payload.pendingPenalty - Penalty amount to pay
     * @param {number} payload.pendingDebits - Outstanding debits
     * @param {string} payload.paymentMethod - Payment method (Cash, UPI, Card, etc.)
     * @param {string} payload.paymentDate - Payment date (YYYY-MM-DD)
     * @param {string} payload.notes - Optional notes
     * @returns {Promise} Response with closure result and receipt
     */
    normalClosureLoan: async (loanApplicationNumber, payload) => {
        try {
            const response = await axiosInstance.post(
                `/emi-closure/${loanApplicationNumber}/normal-closure`,
                payload
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to close loan";
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },

    updateStartDate: async (loanId, startDate) => {
        try {
            const response = await axiosInstance.post(`/loans/${loanId}/update-start-date`, { startDate });
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to update loan start date";
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },

    /**
     * Full close loan with optional discounts
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} payload - Full closure details
     * @param {number} payload.discountPenaltyAmount - Penalty discount amount (optional, default 0)
     * @param {number} payload.discountCollectionAmount - Collection discount amount (optional, default 0)
     * @param {number} payload.discountInterestAmount - Interest discount amount (optional, default 0)
     * @returns {Promise} Response with closure confirmation
     */
    fullCloseLoan: async (loanApplicationNumber, payload) => {
        try {
            const response = await axiosInstance.post(
                `/emi-closure/${loanApplicationNumber}/full-close`,
                payload
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to close loan";
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },

    getClosureAudit: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(
                `/emi-closure/${loanApplicationNumber}/closure-audit`
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch closure audit";
            throw { message: errorMessage, status: error.response?.status };
        }
    },

    /**
     * Fetch the saved Pre Close draft (Date/No. of Months/Interest Rate) if present,
     * otherwise the audit record if already pre-closed via this flow, otherwise defaults
     * @param {string} loanApplicationNumber - Loan application number
     * @returns {Promise} Response with the draft/audit values plus already-paid summary
     */
    getPreCloseScheduleDetails: async (loanApplicationNumber) => {
        try {
            const response = await axiosInstance.get(`/emi-closure/${loanApplicationNumber}/pre-close-details`);
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch pre-close details";
            throw { message: errorMessage, status: error.response?.status };
        }
    },

    /**
     * Simulate the recalculated Pre Close schedule and save it as the loan's draft
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} payload - { closureDate, closureMonths, interestRate }
     * @returns {Promise} Response with the simulated schedule and final payable amount
     */
    simulatePreCloseSchedule: async (loanApplicationNumber, payload) => {
        try {
            const response = await axiosInstance.post(
                `/emi-closure/${loanApplicationNumber}/pre-close-simulate`,
                payload
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to simulate pre-close schedule";
            throw { message: errorMessage, status: error.response?.status };
        }
    },

    /**
     * Execute the Pre Close schedule: deletes the unpaid EMI schedule, inserts the
     * recalculated schedule (paid EMIs untouched), and closes the loan
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} payload - { closureDate, closureMonths, interestRate, remarks }
     * @returns {Promise} Response with the closure confirmation and receipt number
     */
    executePreCloseSchedule: async (loanApplicationNumber, payload) => {
        try {
            const response = await axiosInstance.post(
                `/emi-closure/${loanApplicationNumber}/pre-close-schedule`,
                payload
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to pre-close loan";
            throw { message: errorMessage, status: error.response?.status };
        }
    },

    /**
     * Manually close loan (vehicle sold or write-off scenario)
     * @param {string} loanApplicationNumber - Loan application number
     * @param {Object} payload - Manual closure details
     * @param {number} payload.vehicleSoldAmount - Amount recovered from vehicle sale
     * @param {string} payload.excessCreditTarget - How to apply excess credit (principal|interest), default 'principal'
     * @param {string} payload.paymentMode - Payment method (Cash, UPI, Card, Cheque, BankTransfer)
     * @param {string} payload.paymentDate - Payment date (YYYY-MM-DD or ISO format)
     * @param {number} payload.cashAmount - Cash amount received
     * @param {number} payload.accountAmount - Account amount received
     * @param {string} payload.remarks - Optional remarks (max 500 chars)
     * @returns {Promise} Response with manual closure confirmation
     */
    manualCloseLoan: async (loanApplicationNumber, payload) => {
        try {
            const response = await axiosInstance.post(
                `/emi-closure/${loanApplicationNumber}/manual-close`,
                payload
            );
            return response.data;
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to manually close loan";
            throw {
                message: errorMessage,
                status: error.response?.status,
            };
        }
    },
};

export default LoansService;
